// src/components/UserProfileModal.jsx
//
// Modal popup que muestra el perfil de un colaborador. Se abre al hacer click
// sobre el nombre de un agente/creador/autorizador en TicketDetail, LeaderReports
// u otros lugares. Carga los datos del backend (/api/users/:id/profile) en el
// momento de abrir.
//
// Props:
//   userId   — id del usuario a mostrar (string). Si null/undefined, no abre.
//   onClose  — callback al cerrar el modal.

import React, { useContext, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import api from '../services/api';
import Skeleton from './Skeleton';
import { AuthContext } from '../context/AuthContext';
import { nombreVisible } from '../utils/nombreCorto';

// ── Helpers locales (avatar con iniciales) ───────────────────────────────────
const AVATAR_PALETTE = [
    '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed',
    '#0891b2', '#db2777', '#ea580c', '#65a30d', '#0d9488',
];
function nameToColor(name) {
    if (!name) return '#9ca3af';
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ').filter(Boolean);
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[1][0]).toUpperCase();
}

const ROLE_LABEL = {
    admin:      'Administrador',
    leader:     'Líder',
    agent:      'Agente',
    authorizer: 'Autorizador',
    user:       'Usuario',
};

const ROLE_COLOR = {
    admin:      '#7c3aed',
    leader:     '#0891b2',
    agent:      '#2563eb',
    authorizer: '#d97706',
    user:       '#6b7280',
};

// ── Sub-componentes ───────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--c-border)' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-4)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
            <span style={{ fontSize: 14, color: 'var(--c-text-1)', fontWeight: 500, textAlign: 'right' }}>{value || '—'}</span>
        </div>
    );
}

function StatCard({ label, value, color }) {
    return (
        <div style={{
            background: 'var(--c-msg-bg)',
            border: '1px solid var(--c-border)',
            borderRadius: 10,
            padding: '12px 14px',
            textAlign: 'center',
            borderTop: `3px solid ${color}`,
        }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--c-text-1)', lineHeight: 1 }}>{value ?? '—'}</div>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--c-text-4)', marginTop: 4 }}>{label}</div>
        </div>
    );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function UserProfileModal({ userId, onClose }) {
    const { user: viewer } = useContext(AuthContext);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);

    useEffect(() => {
        if (!userId) {
            setProfile(null);
            setError(null);
            return;
        }
        let alive = true;
        setLoading(true);
        setError(null);
        api.get(`/users/${userId}/profile`)
            .then(res => { if (alive) setProfile(res.data); })
            .catch(err => { if (alive) setError(err.response?.data?.error || 'No se pudo cargar el perfil'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [userId]);

    const show = !!userId;
    // Al solicitante se le muestra el nombre acortado del personal interno.
    const nombreMostrado = profile ? nombreVisible(profile.name, viewer?.role, profile.role) : '';

    return (
        <Modal show={show} onHide={onClose} centered size="md">
            <Modal.Header closeButton style={{ borderBottom: '1px solid var(--c-border)' }}>
                <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Perfil del colaborador</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: '20px 24px' }}>
                {loading && (
                    <div>
                        {/* Avatar + nombre + rol */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <Skeleton variant="circle" width={64} height={64} />
                            <div style={{ flex: 1 }}>
                                <Skeleton variant="line" width="60%" height={18} />
                                <div style={{ marginTop: 8 }}>
                                    <Skeleton variant="line" width="35%" height={12} />
                                </div>
                            </div>
                        </div>
                        {/* Filas */}
                        {[0, 1, 2, 3, 4].map(i => (
                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--c-border)' }}>
                                <Skeleton variant="line" width={80} height={10} />
                                <Skeleton variant="line" width={140} height={12} />
                            </div>
                        ))}
                    </div>
                )}
                {error && !loading && (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#dc2626' }}>
                        {error}
                    </div>
                )}
                {profile && !loading && !error && (
                    <>
                        {/* Avatar + nombre + rol */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: nameToColor(nombreMostrado), color: '#fff',
                                fontWeight: 700, fontSize: 24,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {getInitials(nombreMostrado)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--c-text-1)', lineHeight: 1.2 }}>
                                    {nombreMostrado}
                                </div>
                                <div style={{
                                    display: 'inline-block',
                                    marginTop: 6,
                                    background: ROLE_COLOR[profile.role] + '22',
                                    color: ROLE_COLOR[profile.role],
                                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                                    padding: '2px 10px', borderRadius: 10,
                                }}>
                                    {ROLE_LABEL[profile.role] || profile.role}
                                </div>
                                {!profile.active && (
                                    <span style={{
                                        marginLeft: 6,
                                        background: '#fee2e2', color: '#991b1b',
                                        fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
                                    }}>
                                        Inactivo
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Datos básicos */}
                        <div style={{ marginBottom: profile.stats ? 20 : 0 }}>
                            <InfoRow label="Puesto"   value={profile.puesto} />
                            <InfoRow label="Unidad"   value={profile.unidad} />
                            <InfoRow label="Sucursal" value={profile.sucursal} />
                            <InfoRow label="País"     value={profile.pais} />
                            <InfoRow
                                label="Departamentos"
                                value={(profile.departments || []).map(d => d.name).join(', ') || null}
                            />
                        </div>

                        {/* Stats (solo si vienen) */}
                        {profile.stats && (
                            <>
                                <div style={{
                                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1,
                                    color: 'var(--c-text-4)', marginBottom: 10,
                                }}>
                                    Tickets asignados
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
                                    <StatCard label="Total"      value={profile.stats.totalTickets}    color="#6b7280" />
                                    <StatCard label="Abiertos"   value={profile.stats.openTickets}     color="#2563eb" />
                                    <StatCard label="Progreso"   value={profile.stats.progressTickets} color="#d97706" />
                                    <StatCard label="Cerrados"   value={profile.stats.closedTickets}   color="#059669" />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    <StatCard
                                        label="CSAT promedio"
                                        value={profile.stats.avgCsat != null ? profile.stats.avgCsat.toFixed(1) + ' ★' : '—'}
                                        color="#f59e0b"
                                    />
                                    <StatCard
                                        label="Respuestas CSAT"
                                        value={profile.stats.csatCount}
                                        color="#8b5cf6"
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}
